import { PageOrderEnum } from "@/lib/enum/pageOrder.enum";
import eventService from "@/services/mrfiktiv/services/eventService";
import {
  MrfiktivCreateEventDtoGen,
  MrfiktivEventControllerGetParamsGen,
  MrfiktivUpdateEventDtoGen,
  MrfiktivArchiveEventInstanceDtoGen,
  MrfiktivArchiveVirtualEventDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Action, getModule, Module, Mutation } from "vuex-module-decorators";
import { BasePagination, FilterTypes, IPaginationFilterListElement } from "./base-pagination.store";
import { EventUIDto } from "@/lib/dto/event/event-ui.dto";
import { FleetAggregationModule } from "./fleet-aggregation.store";

@Module({
  dynamic: true,
  namespaced: true,
  name: "event-field",
  store
})
export class EventStore extends BasePagination<EventUIDto, MrfiktivEventControllerGetParamsGen> {
  protected _pageOrder: PageOrderEnum = PageOrderEnum.DESCENDING;
  protected _itemsPerPage = 100;
  protected _totalPages = 0;
  protected _paginationList: EventUIDto[] = [];
  protected _currentPage = 1;
  protected _totalItems = 0;
  protected _isLoadAll = true;
  filterOptions: IPaginationFilterListElement[] = [
    { key: "_id", type: FilterTypes.OBJECT_ID },
    { key: "userId", type: FilterTypes.OBJECT_ID },
    { key: "vehicleId", type: FilterTypes.OBJECT_ID },
    { key: "isAllDay", type: FilterTypes.BOOLEAN },
    { key: "isRecurringRoot", type: FilterTypes.BOOLEAN }
  ];

  // Key is vehicleId
  private _vehicleEventsMap: Map<string, EventUIDto[]> = new Map();
  private _idMap = new Map<string, EventUIDto>();

  protected _documentMapConfig = { id: this._idMap };

  private _event: EventUIDto | undefined = undefined;

  get events() {
    return this._paginationList;
  }

  get vehicleEventLookup() {
    return this._vehicleEventsMap;
  }

  @Mutation
  private _mutateEvent(request: EventUIDto) {
    this._event = request;
  }

  @Mutation
  private _mutateLookup(response: EventUIDto[]) {
    const map = new Map<string, EventUIDto[]>();

    for (const res of response) {
      if (!res.vehicleId) {
        continue;
      }

      const events = map.get(res.vehicleId);
      if (events) {
        events.push(res);
      } else {
        map.set(res.vehicleId, [res]);
      }
    }

    this._vehicleEventsMap = map;
  }

  @Action
  protected async loadDocuments(query: MrfiktivEventControllerGetParamsGen) {
    const res = await eventService.getAll({ ...query });

    return res;
  }

  @Action
  async create(data: { partnerId: string; data: MrfiktivCreateEventDtoGen }) {
    const res = await eventService.create(data.partnerId, data.data);

    this.paginationList.unshift(res);
    this.context.commit("_mutateEvent", res);

    return res;
  }

  @Action
  async delete(data: { partnerId: string; eventId: string }) {
    const res = await eventService.delete(data.partnerId, data.eventId);

    this.removeInList(res);
    this.context.commit("_mutateEvent", res);

    return res;
  }

  @Action
  async getOne(data: { partnerId: string; eventId: string }) {
    const res = await eventService.getOne(data.partnerId, data.eventId);

    this.replaceInList(res);
    this.context.commit("_mutateEvent", res);

    return res;
  }

  @Action
  async update(data: { partnerId: string; eventId: string; data: MrfiktivUpdateEventDtoGen }) {
    const res = await eventService.update(data.partnerId, data.eventId, data.data);

    this.replaceInList(res);
    this.context.commit("_mutateEvent", res);

    return res;
  }

  @Action
  async getAll(data: { partnerId: string }) {
    await this.fetchAllFromBeginning({
      partnerId: data.partnerId
    });
  }

  @Action
  async archiveMany(data: {
    partnerId: string;
    dto: (MrfiktivArchiveVirtualEventDtoGen | MrfiktivArchiveEventInstanceDtoGen)[];
  }) {
    const updated = await eventService.archiveMany(data.partnerId, data.dto);

    return updated;
  }

  @Action
  async deleteMany(data: { partnerId: string; events: EventUIDto[] }) {
    await eventService.deleteMany(data.partnerId, data.events.filter(e => e.id).map(e => e.id) as string[]);
    for (const event of data.events) {
      FleetAggregationModule.removeEvent(event);
    }
  }
}

export const EventModule = getModule(EventStore);
